import { Trans } from '@lingui/react'
import {
  OfferConditionTypeEnum,
  OfferDiscountTypeEnum,
  type SellerPromotionV1,
} from '@redteclab/api/clients/bully'
import { type ReactNode } from 'react'

import { TENANT_AT, TENANT_BE, TENANT_COM, TENANT_IT } from 'shared/consts'
import {
  EXPERIMENT_VARIATION,
  MARKETPLACE_HARMONIZATION,
} from 'shared/experiments/consts'
import { useGetExperiment } from 'shared/experiments/utils/experiments'
import { type PublicRuntimeConfig } from 'types/shopConfig'
import IconArrowRight from 'views/components/atoms/Icon/IconArrowRight'
import LinkWithIcon from 'views/components/molecules/LinkWithIcon'
import Notification from 'views/components/molecules/Notification'
import useMountPoints from 'views/enhancers/useMountPoints'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'

interface messageSettingsProps {
  headline: ReactNode
  translation: ReactNode
  link: boolean
}

const getFormattedDiscountAmount = (sellerPromotion: SellerPromotionV1) => {
  switch (sellerPromotion.discountType) {
    case 'totalAmount': {
      const totalAmountValue = sellerPromotion.discountValue.toFixed(0)
      return `${totalAmountValue}€`
    }
    case 'totalPercent': {
      const percValue = sellerPromotion.discountValue * 100
      return `${percValue.toFixed(0)}%`
    }
    case 'freeItems':
      if (sellerPromotion.conditionType === 'minimumSameItemQuantity') {
        return `${
          sellerPromotion.conditionValue - sellerPromotion.discountValue
        }`
      }
      if (sellerPromotion.conditionType === 'minimumQuantity') {
        return `${sellerPromotion.discountValue}`
      }
      break
    default:
      return ''
  }
}

const getFormattedMovAmount = (sellerPromotion: SellerPromotionV1) => {
  if (sellerPromotion.minimumOrderValue) {
    return sellerPromotion.minimumOrderValue + '€'
  }
  if (sellerPromotion.conditionValue) {
    return sellerPromotion.conditionValue + '€'
  }
}

export const getLegalSupNote = (tenant: string) => {
  if (tenant.includes(TENANT_COM)) {
    return 13
  }
  if (tenant.includes(TENANT_AT)) {
    return 16
  }
  if (tenant.includes(TENANT_BE)) {
    return 3
  }
  if (tenant.includes(TENANT_IT)) {
    return 11
  }

  return 13
}

const GetSellerPromotionNotification = (props: {
  sellerPromotion: SellerPromotionV1
  sellerName: string
  sellerId: string
  publicRuntimeConfig: PublicRuntimeConfig
}): ReactNode => {
  const {
    sellerId,
    sellerName,
    sellerPromotion,
    publicRuntimeConfig: {
      publicConfig: { tenant },
    },
  } = props

  const mountPoints = useMountPoints()

  const messageSettings: messageSettingsProps = {
    headline: null,
    translation: null,
    link: false,
  }

  const marketplaceHarmonization = useGetExperiment(MARKETPLACE_HARMONIZATION)

  const isMarketplaceHarmonizationEnabled =
    marketplaceHarmonization.isEnabled &&
    marketplaceHarmonization.variant === EXPERIMENT_VARIATION.V1

  const isDiscountTypeTotalAmountOrPercent =
    sellerPromotion.discountType === OfferDiscountTypeEnum.TotalAmount ||
    sellerPromotion.discountType === OfferDiscountTypeEnum.TotalPercent

  const conditionValue = sellerPromotion?.conditionValue
  const discountValue = getFormattedDiscountAmount(sellerPromotion)

  const legalSup = getLegalSupNote(tenant)

  switch (sellerPromotion.conditionType) {
    case OfferConditionTypeEnum.MinimumSameItemQuantity:
      if (sellerPromotion.discountType === OfferDiscountTypeEnum.FreeItems) {
        messageSettings.headline = (
          <Trans
            id="seller.promotion.sellerBanner.title.minimumSameItemQuantity.freeItems"
            values={{
              formattedConditionAmount: conditionValue,
              formattedDiscountAmount: discountValue,
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
        messageSettings.translation = (
          <Trans
            id="seller.promotion.sellerBanner.message.minimumSameItemQuantity.freeItems"
            values={{
              formattedConditionAmount: conditionValue,
              formattedDiscountAmount: discountValue,
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
      } else if (
        sellerPromotion.discountType === OfferDiscountTypeEnum.TotalAmount
      ) {
        messageSettings.headline = (
          <Trans
            id="seller.promotion.sellerBanner.title.minimumSameItemQuantity.total"
            values={{
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
        messageSettings.translation = (
          <Trans
            id="seller.promotion.sellerBanner.message.minimumSameItemQuantity.totalAmount"
            values={{
              formattedConditionAmount: conditionValue,
              formattedDiscountAmount: discountValue,
            }}
          />
        )
      } else if (
        sellerPromotion.discountType === OfferDiscountTypeEnum.TotalPercent
      ) {
        messageSettings.headline = (
          <Trans
            id="seller.promotion.sellerBanner.title.minimumSameItemQuantity.total"
            values={{
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
        messageSettings.translation = (
          <Trans
            id="seller.promotion.sellerBanner.message.minimumSameItemQuantity.totalPercent"
            values={{
              formattedConditionAmount: conditionValue,
              formattedDiscountAmount: discountValue,
            }}
          />
        )
      }
      break
    case OfferConditionTypeEnum.MinimumQuantity:
      messageSettings.link = true
      if (sellerPromotion.discountType === OfferDiscountTypeEnum.FreeItems) {
        messageSettings.headline = (
          <Trans
            id="seller.promotion.sellerBanner.title.minimumQuantity.freeItem"
            values={{
              formattedConditionAmount: conditionValue,
              formattedDiscountAmount: discountValue,
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
        messageSettings.translation = isMarketplaceHarmonizationEnabled ? (
          <Trans
            id="seller.promotion.sellerBanner.message.minimumQuantity.freeItems"
            values={{
              formattedConditionAmount: conditionValue,
              sellerName,
            }}
          />
        ) : (
          <Trans
            id="seller.promotion.sellerBanner.message.minimumQuantity.freeItem"
            values={{
              formattedConditionAmount: conditionValue,
              sellerName,
            }}
          />
        )
      } else if (
        sellerPromotion.discountType === OfferDiscountTypeEnum.TotalAmount
      ) {
        messageSettings.headline = (
          <Trans
            id="seller.promotion.sellerBanner.title.minimumQuantity.total"
            values={{
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
        messageSettings.translation = (
          <Trans
            id="seller.promotion.sellerBanner.message.minimumQuantity.totalAmount"
            values={{
              formattedConditionAmount: conditionValue,
              formattedDiscountAmount: discountValue,
              sellerName,
            }}
          />
        )
      } else if (
        sellerPromotion.discountType === OfferDiscountTypeEnum.TotalPercent
      ) {
        messageSettings.headline = (
          <Trans
            id="seller.promotion.sellerBanner.title.minimumQuantity.total"
            values={{
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
        messageSettings.translation = (
          <Trans
            id="seller.promotion.sellerBanner.message.minimumQuantity.totalPercent"
            values={{
              formattedConditionAmount: conditionValue,
              formattedDiscountAmount: discountValue,
              sellerName,
            }}
          />
        )
      }
      break
    case OfferConditionTypeEnum.MinimumOrderValue:
      messageSettings.link = true

      if (
        isMarketplaceHarmonizationEnabled &&
        isDiscountTypeTotalAmountOrPercent
      ) {
        messageSettings.headline = (
          <Trans
            id="seller.promotion.sellerBanner.title.mov.total"
            values={{
              formattedMovAmount: discountValue,
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
        messageSettings.translation = (
          <Trans
            id="seller.promotion.sellerBanner.message.mov.total"
            values={{
              formattedMovAmount: getFormattedMovAmount(sellerPromotion),
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
      } else {
        messageSettings.headline = (
          <Trans
            id="seller.promotion.sellerBanner.title"
            values={{
              formattedMovAmount: discountValue,
            }}
          />
        )
        messageSettings.translation = (
          <Trans
            id="seller.promotion.sellerBanner.message"
            values={{
              formattedMovAmount: getFormattedMovAmount(sellerPromotion),
            }}
          />
        )
      }
      break
    default:
      break
  }

  return (
    <Notification
      alignIcon="top"
      className="u-background-color--background"
      dataQaId={`seller-not-applied-promotion-banner-${sellerId}`}
      heading={messageSettings.headline}
      label={
        <div>
          <div
            className="u-margin-x-small--bottom"
            data-qa-id={`seller-not-applied-promotion-banner-message-${sellerId}`}
          >
            {messageSettings.translation}
          </div>
          {messageSettings.link &&
            (isMarketplaceHarmonizationEnabled &&
            (isDiscountTypeTotalAmountOrPercent ||
              sellerPromotion.discountType ===
                OfferDiscountTypeEnum.FreeItems) ? (
              <LinkWithIcon
                className="o-Cart-Notification-link-marketplace-promotions"
                data-qa-id={`seller-not-applied-promotion-banner-serp-link-${sellerId}`}
                href={mountPoints.getSellerProducts(sellerId)}
                icon={<IconArrowRight />}
                right
              >
                <span className="u-font-weight--semi-bold">
                  <Trans id="seller.promotion.product.banner.sellerSerpLink" />
                </span>
              </LinkWithIcon>
            ) : (
              <a
                href={mountPoints.getSellerProducts(sellerId)}
                className="o-Cart-Notification-link"
                data-qa-id={`seller-not-applied-promotion-banner-serp-link-${sellerId}`}
              >
                <Trans id="seller.promotion.product.banner.sellerSerpLink" />
              </a>
            ))}
        </div>
      }
      type={
        isMarketplaceHarmonizationEnabled ? 'infoPromotion' : 'infoNoBgColor'
      }
    />
  )
}

export default withShopAndRuntimeConfig(GetSellerPromotionNotification)
